import { Injectable } from '@angular/core';
// import * as _moment from 'moment';
import type { Moment } from 'moment';
// const moment = _moment;
import moment from 'moment';
import 'moment-timezone';
import { MomentTimeZoneEnum } from '../enums/moment-timezone.enum';
import { environment } from 'src/environments/environment';
import { ReducerUtilityService } from '../utility-services/reducer-utility.service';
// Pull out DateService functions so we can use it in Reducer files without initiating the service. Add to this only as needed.
import * as DateFunctions from './date.functions'
import { Observable } from "rxjs";
import { NgRedux, select } from '@ptl/angular-redux-store';
import { resetDirtyEffectiveDate, setDirtyEffectiveDate } from 'src/app/angular-elements/shared/components/configs/reducers/effective-date.reducer';
import { StateStructure } from '@ptl/state';
/**
 * @Description Helps to reduce necessity of Moment knowledge and Timezone issues
 */
@Injectable({
  providedIn: 'root'
})
export class DateService {

  currentTimeZone = MomentTimeZoneEnum[environment.configs.siteTimeZone];
  @select(['appStates', 'EffectiveDate']) effectiveDate$: Observable<any>;

  constructor(
    private ngRedux: NgRedux<StateStructure>
  ) {

  }

  setDirtyEffectiveDate(resetDirty?: boolean) {
    if (resetDirty === true) {
      this.ngRedux.dispatch(resetDirtyEffectiveDate());
    } else {
      this.ngRedux.dispatch(setDirtyEffectiveDate());
    }
  }
  /**
   * Retrieves now from a Service
   * @param format Example: 'YYYY-MM-DD'
   */
  getNowFromService() {
    // TODO: Add service call and fallbacks if service call fails.
    const virtualNow = moment().tz(this.currentTimeZone);
    return virtualNow;
  }

  /**
   * Gets Now, whether from Date Offset AppState Now or from Service.
   * @param format Example: 'YYYY-MM-DD'
   */
  getNow() {
    const nowState = window.sessionStorage.getItem('dateOffset') || 'now';
    return nowState === 'now' ? this.getNowFromService() : moment(nowState);
  }

  /**
   * Generates provided date in a timezone localized date of a specified format
   * @param format Example: 'YYYY-MM-DD'
   */
  getDateAsFormattedString(date: string | Date, format: string): string {
    return this.getDateAsMoment(date).format(format);
  }

  /**
   * Generates a timezone localized date of Moment format
   */
  getTodayAsMoment(): Moment {
    return moment(this.getNow()).tz(this.currentTimeZone);
  }

  getDateAsMoment(date: string | Date | moment.Moment): Moment {

    const dateAsMoment = moment.parseZone(date);

    return dateAsMoment;

  }

  /**
   * Generates today's date in a timezone localized date of a specified format
   * @param format Example: 'YYYY-MM-DD'
   */
  getTodayAsFormattedString(format: string): string {
    return this.getTodayAsMoment().format(format);
  }

  monthsRemainingForYear(year: string | number): number {
    let today = this.getTodayAsMoment();

    // If it's this year, subtract 12 from the current month ID+1 for the number of months remaining
    if (today.year() == year) {
      return (12 - (today.month() + 1));
    }
    // If it's next year, we use all 12 months in our calculations
    else {
      return 12;
    }

  }

  getFirstOfNextMonth(dateOverride?) {
    const now: Moment = dateOverride ? moment(dateOverride) : this.getTodayAsMoment();
    return DateFunctions.getFirstOfNextMonth(now);
  }

  isValidDate(dateToCheck) {
    return moment(dateToCheck).isValid();
  }

  getNullOrFormattedDate(date: any, formattedString: string) {
    if (!date || !this.isValidDate(date)) {
      return null;
    }

    return this.getDateAsFormattedString(date, formattedString);
  }

  // Coverage date can be null or in the future, but it can't be in the past
  getValidCoverageDate(coverageDate: string) {
    let newCoverageDate = null;

    if (coverageDate) {
      newCoverageDate = this.getDateAsFormattedString(coverageDate, 'MM/DD/YYYY');

      // Loading an old app could put the coverage date before the submission, so move it up a month if that is the case
      if (new Date() >= new Date(newCoverageDate)) {
        newCoverageDate = this.getFirstOfNextMonth().format('MM/DD/YYYY');
      }
    }

    return newCoverageDate;
  }

  oneMonthIntoFutureDate(): string {
    return moment().add(1, 'months').startOf('month').format('MM/DD/YYYY');
  }

  getEffectiveDate(effectiveDate) {
    if (effectiveDate.split) {
      return effectiveDate.split('-')[0];
    } else {
      return effectiveDate;
    }
  }

  /**
   * @description returns the same month and day of the provided date, but the year will be updated to the current year
   * 
   * @param dateString a formatted date string
   * 
   * @returns A string representing the same month and day but with the current year
   */
  getDateAsCurrentYearDate(date: string): string {
    const currentYear = this.getTodayAsMoment().year();
    const inputDate = this.getDateAsMoment(date);
    
    if (inputDate && inputDate.isValid()) {
      return inputDate.year(currentYear).format('YYYY-MM-DD');
    }
    
    return date;
  }
}
